import { ServiceCard } from '@/components/prestations/ServiceCard';
import { NewsletterHero } from '@/components/home/NewsletterHero';
import { TrustedByBanner } from '@/components/home/TrustedByBanner';
import { SEOHead } from '@/components/SEO/SEOHead';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

export function PrestationsPage() {
  const { t, i18n } = useTranslation('prestations');
  const { locale } = useParams<{ locale: string }>();
  const currentLocale = locale || i18n.language || 'fr';

  const services = [
    {
      title: t('services.dataScience.title'),
      description: t('services.dataScience.description'),
      features: t('services.dataScience.features', { returnObjects: true }) as string[],
      iconType: 'data-science' as const,
      color: '#55BC8C',
      slug: 'data-science',
    },
    {
      title: t('services.dossiers.title'),
      description: t('services.dossiers.description'),
      features: t('services.dossiers.features', { returnObjects: true }) as string[],
      iconType: 'dossiers' as const,
      color: '#9B59B6',
      slug: 'dossiers-techniques',
      badge: t('serviceCard.badge.popular'),
    },
    {
      title: t('services.infographies.title'),
      description: t('services.infographies.description'),
      features: t('services.infographies.features', { returnObjects: true }) as string[],
      iconType: 'infographies' as const,
      color: '#E96656',
      slug: 'infographies',
    },
    {
      title: t('services.formations.title'),
      description: t('services.formations.description'),
      features: t('services.formations.features', { returnObjects: true }) as string[],
      iconType: 'formations' as const,
      color: '#0D974E',
      slug: '/formations',
    },
    {
      title: t('services.audit.title'),
      description: t('services.audit.description'),
      features: t('services.audit.features', { returnObjects: true }) as string[],
      iconType: 'audit' as const,
      color: '#F9A826',
      slug: 'audit-expertise',
    },
    {
      title: t('services.interventions.title'),
      description: t('services.interventions.description'),
      features: t('services.interventions.features', { returnObjects: true }) as string[],
      iconType: 'interventions' as const,
      color: '#8B6F47',
      slug: 'interventions',
    },
  ];
  return (
    <div>
      <SEOHead
        title={t('seo.title', 'Prestations - Aspexit')}
        description={t('seo.description', 'Conseil et expertise en agriculture numerique')}
        path="/prestations"
      />
      {/* Hero */}
      <section className="bg-[#F3EEEC] py-12">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            {/* Left: Title & Description */}
            <div>
              <h1 className="text-4xl md:text-5xl font-heading font-extrabold mb-4">
                <span className="text-aspexit-black">{t('page.hero.title')}</span>{' '}
                <span className="text-aspexit-black">{t('page.hero.titleAccent')}</span>
              </h1>
              <p className="text-lg text-aspexit-gray mb-4">
                {t('page.hero.subtitle')}
              </p>
              <p className="text-aspexit-gray text-sm">
                {t('page.hero.tagline')}
              </p>
            </div>

            {/* Right: Stats Cards */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white rounded-lg p-3 border border-border shadow-sm">
                <div className="text-2xl font-bold text-aspexit-green mb-0.5">6</div>
                <div className="text-xs text-aspexit-gray">{t('page.hero.stats.services')}</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-border shadow-sm">
                <div className="text-2xl font-bold text-aspexit-green mb-0.5">10+</div>
                <div className="text-xs text-aspexit-gray">{t('page.hero.stats.experience')}</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-border shadow-sm">
                <div className="text-2xl font-bold text-aspexit-green mb-0.5">100+</div>
                <div className="text-xs text-aspexit-gray">{t('page.hero.stats.projects')}</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-border shadow-sm">
                <div className="text-xl font-bold text-aspexit-green mb-0.5">QUALIOPI</div>
                <div className="text-xs text-aspexit-gray">{t('page.hero.stats.qualiopi')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <ServiceCard key={service.slug} {...service} />
            ))}
          </div>
        </div>
      </section>

      {/* Clients / Partners */}
      <TrustedByBanner />

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-aspexit-green to-aspexit-green-dark text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-heading font-bold mb-4">
              {t('page.cta.title')}
            </h2>
            <p className="text-xl mb-8 opacity-90">
              {t('page.cta.subtitle')}
            </p>

            <div className="flex justify-center">
              <a
                href={`/${currentLocale}/contact`}
                className="px-8 py-4 bg-white text-aspexit-green rounded-lg font-semibold hover:bg-aspexit-gray-light transition-colors shadow-lg"
              >
                {t('page.cta.button')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <NewsletterHero />
    </div>
  );
}
